import {
  React,
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import { useDebounce } from "use-lodash-debounce";

const DEBOUNCE_SEARCH_TIME = 600;

// GlobalSearchDefaultScopeContext
// The default scope state is separated from the SearchContext because it is used in many places to one-off set the
// default scope but we don't want to re-render all those components every time the search query/data/results changes
export const GlobalSearchDefaultScopeContext = createContext();

export const useGlobalSearchDefaultScopeContext = () =>
  useContext(GlobalSearchDefaultScopeContext);

export const GlobalSearchDefaultScopeProvider = ({ children }) => {
  const [defaultSearchScope, setDefaultSearchScope] = useState();

  return (
    <GlobalSearchDefaultScopeContext.Provider
      value={{
        defaultSearchScope,
        setDefaultSearchScope,
      }}
    >
      {children}
    </GlobalSearchDefaultScopeContext.Provider>
  );
};

GlobalSearchDefaultScopeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// GlobalSearchContext
// (depends on GlobalSearchDefaultScopeContext)
// Provides search configuration (filters, team types) and UI state (input query).
// Search results and loading state are handled by useGlobalSearch hook.

export const GlobalSearchContext = createContext();

export const useGlobalSearchContext = () => useContext(GlobalSearchContext);

export const GlobalSearchProvider = ({ children }) => {
  const { defaultSearchScope, setDefaultSearchScope } =
    useGlobalSearchDefaultScopeContext();
  const [peopleFilters, setPeopleFilters] = useState([]);
  const [groupFilters, setGroupFilters] = useState([]);
  const [defaultTeamTypes, setDefaultTeamTypes] = useState([]);
  const [userSearchInputQuery, setUserSearchInputQuery] = useState();
  const [searchQuery, setSearchQuery] = useState();

  const addPeopleFilter = useCallback((filter) => {
    setPeopleFilters((prev) => [...prev, filter]);
  }, []);

  const removePeopleFilter = useCallback((filter) => {
    setPeopleFilters((prev) => prev.filter((item) => item !== filter));
  }, []);

  const addGroupFilter = useCallback((filter) => {
    setGroupFilters((prev) => [...prev, filter]);
  }, []);

  const removeGroupFilter = useCallback((filter) => {
    setGroupFilters((prev) => prev.filter((item) => item !== filter));
  }, []);

  const addDefaultTeamType = useCallback(
    (teamType) => {
      setDefaultTeamTypes([...defaultTeamTypes, teamType]);
    },
    [defaultTeamTypes]
  );

  const removeDefaultTeamType = useCallback(
    (teamType) => {
      setDefaultTeamTypes(defaultTeamTypes.filter((item) => item !== teamType));
    },
    [defaultTeamTypes]
  );

  const debouncedSearchQuery = useDebounce(searchQuery, DEBOUNCE_SEARCH_TIME);

  const value = useMemo(() => {
    return {
      defaultSearchScope,
      setDefaultSearchScope,
      searchQuery: debouncedSearchQuery,
      setSearchQuery,
      peopleFilters,
      setPeopleFilters,
      groupFilters,
      setGroupFilters,
      defaultTeamTypes,
      setDefaultTeamTypes,
      addPeopleFilter,
      removePeopleFilter,
      addGroupFilter,
      removeGroupFilter,
      addDefaultTeamType,
      removeDefaultTeamType,
      userSearchInputQuery,
      setUserSearchInputQuery,
    };
  }, [
    defaultSearchScope,
    setDefaultSearchScope,
    debouncedSearchQuery,
    peopleFilters,
    groupFilters,
    defaultTeamTypes,
    addPeopleFilter,
    removePeopleFilter,
    addGroupFilter,
    removeGroupFilter,
    addDefaultTeamType,
    removeDefaultTeamType,
    userSearchInputQuery,
  ]);

  return (
    <GlobalSearchContext.Provider value={value}>
      {children}
    </GlobalSearchContext.Provider>
  );
};

GlobalSearchProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
